
import React, { createContext, useContext, useState, useCallback } from 'react';
import { LanguageContextProps, Language } from './types';
import { translations } from './translations';

// Create the context with an initial default value
const LanguageContext = createContext<LanguageContextProps>({
  language: 'en',
  t: (key) => key,
  toggleLanguage: () => {},
  setLanguage: () => {},
});

// Define the LanguageProvider component
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  // Toggle between English and Spanish
  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  }, []);

  // Define the translation function
  const t = useCallback((key: string, args: Record<string, string | number> = {}) => {
    // Get the translation for the specified key and language
    const translation = translations[language]?.[key] || key;
    
    // Replace arguments in the translation string
    let translatedText = translation;
    Object.entries(args).forEach(([argKey, argValue]) => {
      translatedText = translatedText.replace(`{${argKey}}`, String(argValue));
    });
    
    return translatedText;
  }, [language]);

  // Create the context value
  const value = {
    language,
    t,
    toggleLanguage,
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
